import type { NotificationPreferences } from "../types"
import { buildQuietHoursPayload, parseQuietHours } from "../utils/notifications"

export type NotificationChannelKey = "sms" | "email" | "web"

export type NotificationPreferencesState = {
  weeklyDigest: boolean
  channels: Record<NotificationChannelKey, boolean>
  quietHoursEnabled: boolean
  quietHoursStart: string
  quietHoursEnd: string
}

export type NotificationPreferencesAction =
  | { type: "hydrate"; payload: NotificationPreferences | null | undefined }
  | { type: "toggleChannel"; channel: NotificationChannelKey; value: boolean }
  | { type: "setWeeklyDigest"; value: boolean }
  | { type: "setQuietHours"; enabled: boolean; start?: string; end?: string }

export const DEFAULT_NOTIFICATION_PREFERENCES_STATE: NotificationPreferencesState = {
  weeklyDigest: false,
  channels: {
    sms: false,
    email: false,
    web: false,
  },
  quietHoursEnabled: false,
  quietHoursStart: "21:00",
  quietHoursEnd: "07:00",
}

export function notificationPreferencesReducer(
  state: NotificationPreferencesState,
  action: NotificationPreferencesAction,
): NotificationPreferencesState {
  switch (action.type) {
    case "hydrate": {
      const prefs = action.payload
      if (!prefs) return state
      const channels: Record<NotificationChannelKey, boolean> = {
        sms: Boolean(prefs.channels.sms?.digest_enabled),
        email: Boolean(prefs.channels.email?.digest_enabled),
        web: Boolean(prefs.channels.web?.digest_enabled),
      }
      const quiet = parseQuietHours(prefs.channels.sms?.quiet_hours ?? null)
      return {
        weeklyDigest: prefs.weekly_digest,
        channels,
        quietHoursEnabled: quiet.enabled,
        quietHoursStart: quiet.start,
        quietHoursEnd: quiet.end,
      }
    }
    case "toggleChannel": {
      return {
        ...state,
        channels: {
          ...state.channels,
          [action.channel]: action.value,
        },
      }
    }
    case "setWeeklyDigest": {
      return {
        ...state,
        weeklyDigest: action.value,
      }
    }
    case "setQuietHours": {
      return {
        ...state,
        quietHoursEnabled: action.enabled,
        quietHoursStart: action.start ?? state.quietHoursStart,
        quietHoursEnd: action.end ?? state.quietHoursEnd,
      }
    }
    default:
      return state
  }
}

export function buildNotificationPreferencePayload(state: NotificationPreferencesState) {
  return {
    weekly_digest: state.weeklyDigest,
    channels: {
      sms: {
        digest_enabled: state.channels.sms,
        quiet_hours: buildQuietHoursPayload(state.quietHoursEnabled, state.quietHoursStart, state.quietHoursEnd),
      },
      email: {
        digest_enabled: state.channels.email,
      },
      web: {
        digest_enabled: state.channels.web,
      },
    },
  }
}
